import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { voucherTiers, voucherCheckout } from "@/lib/api";

const trackPixel = (event, params = {}) => {
    if (typeof window !== "undefined" && window.fbq) window.fbq("track", event, params);
};

export default function Voucher() {
    const [searchParams] = useSearchParams();
    const [tiers, setTiers] = useState([]);
    const [selectedTier, setSelectedTier] = useState(searchParams.get("tier") || "combo");
    const [form, setForm] = useState({
        purchaser_name: "",
        purchaser_email: "",
        recipient_name: "",
        recipient_email: "",
        message: "",
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        trackPixel("PageView");
        trackPixel("ViewContent", { content_type: "gift_voucher" });
        voucherTiers().then(d => setTiers(d.items || [])).catch(() => toast.error("Errore caricamento tagli"));
    }, []);

    useEffect(() => {
        if (searchParams.get("cancel")) toast.info("Acquisto annullato — riprova quando vuoi");
    }, [searchParams]);

    const current = tiers.find(t => t.id === selectedTier);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.purchaser_name.trim() || !form.purchaser_email.trim()) {
            toast.error("Nome ed email dell'acquirente sono obbligatori");
            return;
        }
        trackPixel("AddToCart", {
            content_ids: [selectedTier],
            content_type: "gift_voucher",
            value: current?.amount_cents / 100,
            currency: "EUR",
        });
        setBusy(true);
        try {
            const { checkout_url } = await voucherCheckout({ tier: selectedTier, ...form });
            window.location.href = checkout_url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore checkout");
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0b0d] text-[#F4F2ED]" data-testid="page-voucher">
            {/* HEADER */}
            <header className="border-b-4 border-[#E2231A]">
                <div className="max-w-4xl mx-auto px-5 md:px-8 py-6 flex items-center justify-between">
                    <a href="/" className="hover:opacity-80 transition"><img src="/logo/velora-horizontal.png" alt="VELORA JDM" className="h-8 md:h-10" /></a>
                    <a href="/riscatta" className="text-xs uppercase tracking-widest text-[#9AA2AD] hover:text-[#F4F2ED] transition" data-testid="link-to-redeem">Ho un codice →</a>
                </div>
            </header>

            {/* HERO */}
            <section className="max-w-4xl mx-auto px-5 md:px-8 pt-12 pb-8 text-center">
                <div className="inline-flex items-center gap-2 border border-[#EA5AC0]/60 bg-[#EA5AC0]/10 px-4 py-2 text-[10px] uppercase tracking-widest mb-6">
                    🎁 REGALO PERFETTO PER APPASSIONATI JDM
                </div>
                <h1 className="font-display text-4xl md:text-6xl uppercase leading-none mb-4" data-testid="voucher-title">
                    Regala l'<span className="text-[#E2231A]">esperienza</span> che ricorderà per sempre
                </h1>
                <p className="text-[#c8ccd1] text-lg max-w-2xl mx-auto">
                    Un vero giro in pista su EVO X o WRX. Voucher digitale, riscattabile entro 12 mesi. Consegnato via email in 2 minuti.
                </p>
            </section>

            {/* MOCKUP CARD */}
            <section className="max-w-3xl mx-auto px-5 md:px-8 pb-10">
                <div className="relative bg-gradient-to-br from-[#15181C] via-[#1a0708] to-[#15181C] border-2 border-[#E2231A] p-6 md:p-8 overflow-hidden">
                    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[#E2231A]/20 blur-3xl" />
                    <div className="flex justify-between items-start mb-6 flex-wrap gap-2">
                        <div>
                            <img src="/logo/velora-horizontal.png" alt="VELORA JDM" className="h-9 mb-1" />
                            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Cofanetto Regalo</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Valore</div>
                            <div className="font-display text-3xl text-[#E2231A]">€{current?.amount_cents / 100 || 129}</div>
                        </div>
                    </div>
                    <div className="mt-6 mb-6 font-display text-2xl md:text-3xl uppercase leading-tight">{current?.label || "Combo Duello"}</div>
                    <div className="grid grid-cols-3 gap-2 text-center pt-6 border-t border-[#9AA2AD]/20">
                        <div>
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Codice</div>
                            <div className="font-mono text-xs">VELORA-XXXX-XXXX</div>
                        </div>
                        <div>
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Validità</div>
                            <div className="text-xs">12 mesi</div>
                        </div>
                        <div>
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Formato</div>
                            <div className="text-xs">PDF + Email</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SCELTA TAGLIO */}
            <section className="max-w-3xl mx-auto px-5 md:px-8">
                <h2 className="font-display text-xl uppercase tracking-widest mb-4">1 · Scegli il taglio</h2>
                <div className="grid md:grid-cols-2 gap-3 mb-8">
                    {tiers.map(t => (
                        <button
                            key={t.id}
                            onClick={() => setSelectedTier(t.id)}
                            data-testid={`tier-btn-${t.id}`}
                            className={`text-left border-2 p-5 transition-all ${selectedTier === t.id ? "border-[#E2231A] bg-[#E2231A]/10" : "border-[#9AA2AD]/20 hover:border-[#9AA2AD]/40"}`}
                        >
                            <div className="flex items-baseline justify-between gap-2 mb-2">
                                <div className="font-display text-xl uppercase">{t.label}</div>
                                <div className="font-display text-2xl text-[#E2231A]">€{t.amount_cents / 100}</div>
                            </div>
                            <div className="text-xs text-[#9AA2AD] mb-2">{t.description}</div>
                            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">
                                Listino <s>€{t.list_cents / 100}</s> · <b className="text-[#16a34a]">50% OFF LANCIO</b>
                            </div>
                        </button>
                    ))}
                </div>

                {/* FORM */}
                <form onSubmit={submit} className="space-y-5 pb-16" data-testid="voucher-form">
                    <h2 className="font-display text-xl uppercase tracking-widest">2 · I tuoi dati</h2>
                    <FormRow label="Nome acquirente" required>
                        <input required data-testid="input-purchaser-name" className="input" placeholder="Mario Rossi" value={form.purchaser_name} onChange={e => setForm(f => ({...f, purchaser_name: e.target.value}))} />
                    </FormRow>
                    <FormRow label="Email acquirente" required hint="Ricevi qui la conferma di acquisto e il PDF voucher">
                        <input required type="email" data-testid="input-purchaser-email" className="input" placeholder="mario@email.com" value={form.purchaser_email} onChange={e => setForm(f => ({...f, purchaser_email: e.target.value}))} />
                    </FormRow>

                    <h2 className="font-display text-xl uppercase tracking-widest pt-4">3 · Chi lo riceve (opzionale)</h2>
                    <p className="text-xs text-[#9AA2AD]">Lascia vuoto se preferisci stampare il PDF e consegnarlo di persona.</p>
                    <FormRow label="Nome destinatario">
                        <input data-testid="input-recipient-name" className="input" placeholder="Luca Bianchi" value={form.recipient_name} onChange={e => setForm(f => ({...f, recipient_name: e.target.value}))} />
                    </FormRow>
                    <FormRow label="Email destinatario" hint="Se compilata, riceverà una email con il codice al momento dell'acquisto">
                        <input type="email" data-testid="input-recipient-email" className="input" placeholder="luca@email.com" value={form.recipient_email} onChange={e => setForm(f => ({...f, recipient_email: e.target.value}))} />
                    </FormRow>
                    <FormRow label="Messaggio personalizzato" hint="Apparirà nell'email e nel PDF">
                        <textarea data-testid="input-message" rows={3} className="input resize-none" placeholder="Buon compleanno! Non vedo l'ora di sentire com'è andata 🏁" value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} maxLength={500} />
                    </FormRow>

                    <div className="border border-[#EA5AC0]/40 bg-[#EA5AC0]/5 p-4">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="bg-[#EA5AC0] px-2 py-1 text-[#0a0b0d] font-black text-xs rounded">Klarna.</div>
                            <div className="text-xs uppercase tracking-widest">Paga in 3 rate</div>
                        </div>
                        <div className="text-xs text-[#c8ccd1]">Selezionabile al checkout · nessun interesse · decisione istantanea</div>
                    </div>

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="btn-voucher-checkout"
                        className="w-full bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-50 text-[#F4F2ED] py-4 uppercase tracking-widest font-display text-lg transition"
                    >{busy ? "Vado al pagamento…" : `🎁 Acquista voucher · €${current?.amount_cents / 100 || 129}`}</button>

                    <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] text-center pt-2">
                        Pagamento sicuro Stripe · Voucher via email in 2 minuti · Valido 12 mesi
                    </div>
                </form>
            </section>

            <footer className="border-t border-[#9AA2AD]/20 bg-[#0a0b0d]">
                <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 text-center text-xs text-[#9AA2AD]">
                    <div className="mb-2">VELORA JDM · Marchio di MAS S.r.l. · P.IVA 01688990629</div>
                    <div className="flex gap-4 justify-center flex-wrap">
                        <a href="/" className="hover:text-[#F4F2ED]">Home</a>
                        <a href="/riscatta" className="hover:text-[#F4F2ED]">Riscatta un codice</a>
                        <a href="/termini" className="hover:text-[#F4F2ED]">Termini</a>
                        <a href="/privacy" className="hover:text-[#F4F2ED]">Privacy</a>
                    </div>
                </div>
            </footer>

            <style>{`
                .input {
                    width: 100%;
                    background: #15181C;
                    border: 1px solid rgba(154,162,173,0.2);
                    padding: 12px 14px;
                    color: #F4F2ED;
                    font-size: 15px;
                    outline: none;
                    transition: border-color .15s;
                }
                .input:focus { border-color: #E2231A; }
            `}</style>
        </div>
    );
}

function FormRow({ label, required, hint, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-2">
                {label}{required && <span className="text-[#E2231A]"> *</span>}
            </div>
            {children}
            {hint && <div className="text-[10px] text-[#9AA2AD]/70 mt-1">{hint}</div>}
        </label>
    );
}
